import { pluralize } from "@/components/helpers/string-utils";
import { Box } from "@/components/layout/flexbox";
import { sortScenariosByDifficulty } from "@/components/pages_logged_in/roleplays/utils";
import { IconCircle } from "@/components/pieces/IconCircle";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import {
  Scenario,
  ScenarioCard,
} from "@/components/pieces/roleplays/ScenarioCard";
import { BLUE_TEXT_COLOR, LIGHT_BLUE } from "@/css/constants";
import { RoleplayRank } from "@/graphql";
import { cn } from "@/lib/utils";
import { ProgramScenario } from "@/types/roleplays";

interface ProgramRoleplayCardProps {
  roleplayScenarios?: ProgramScenario[];
  canDoRoleplay?: boolean;
  hideRoleplayRank?: boolean;
  large?: boolean;
  isProgramLead?: boolean;
  isProgramClosed?: boolean;
  minimumRank?: RoleplayRank | null;
  minimumAttempts?: number | null;
  maximumAttempts?: number | null;
  scenarioRequiredNum?: number | null;
  hasCompletedPrerequisites?: boolean;
}

const RANK_OPTIONS = {
  [RoleplayRank.Bronze]: {
    label: <span className="text-bronze font-semibold">Bronze</span>,
    icon: (
      <RankIcon
        className="inline-block"
        rank={RoleplayRank.Bronze}
        type="circle"
        size={21.5}
      />
    ),
  },
  [RoleplayRank.Silver]: {
    label: <span className="text-silver font-semibold">Silver</span>,
    icon: (
      <RankIcon
        className="inline-block"
        rank={RoleplayRank.Silver}
        type="circle"
        size={21.5}
      />
    ),
  },
  [RoleplayRank.Gold]: {
    label: <span className="text-gold font-semibold">Gold</span>,
    icon: (
      <RankIcon
        className="inline-block"
        rank={RoleplayRank.Gold}
        type="circle"
        size={21.5}
      />
    ),
  },
  [RoleplayRank.Unranked]: undefined,
};

const getCompletionFrequency = ({
  minimumAttempts,
  maximumAttempts,
}: {
  minimumAttempts: number | null | undefined;
  maximumAttempts: number | null | undefined;
}) => {
  if (!minimumAttempts && !maximumAttempts) {
    return undefined;
  }

  if (!minimumAttempts) {
    return `within ${pluralize({
      amount: maximumAttempts,
      singular: "attempt",
      plural: "attempts",
    })}`;
  }

  if (!maximumAttempts) {
    return `at least ${pluralize({
      amount: minimumAttempts,
      singular: "once",
      plural: "times",
      showCount: minimumAttempts > 1,
    })}`;
  }

  if (minimumAttempts === maximumAttempts && minimumAttempts === 1) {
    return "once";
  }

  if (minimumAttempts === maximumAttempts) {
    return `exactly ${pluralize({
      amount: minimumAttempts,
      singular: "time",
      plural: "times",
    })}`;
  }

  return `${minimumAttempts}-${maximumAttempts} ${pluralize({
    amount: maximumAttempts,
    singular: "time",
    plural: "times",
    showCount: false,
  })}`;
};

export const getRoleplayCompletionInstructions = ({
  maximumAttempts,
  minimumAttempts,
  numOfRequiredScenarios,
  numOfScenarios,
  minimumRank,
}: {
  maximumAttempts: number | null | undefined;
  minimumAttempts: number | null | undefined;
  numOfRequiredScenarios: number | null | undefined;
  numOfScenarios: number;
  minimumRank?: RoleplayRank | null;
}) => {
  const rankOption = minimumRank ? RANK_OPTIONS[minimumRank] : undefined;

  const action =
    rankOption && !minimumAttempts && !maximumAttempts ? "Score" : "Practice";

  const scenarioCount =
    numOfScenarios === 1
      ? "the scenario"
      : numOfRequiredScenarios && numOfRequiredScenarios < numOfScenarios
        ? `${numOfRequiredScenarios} of ${numOfScenarios} scenarios`
        : "all scenarios";

  const frequency = getCompletionFrequency({
    minimumAttempts: minimumAttempts,
    maximumAttempts: maximumAttempts,
  });

  const rankInfo = rankOption ? (
    <>
      {rankOption.icon}&nbsp;{rankOption.label}
      {minimumRank !== RoleplayRank.Gold ? <span>&nbsp;or higher</span> : ""}
    </>
  ) : null;

  const scoreRequirement = rankInfo ? (
    <span>, score&nbsp;{rankInfo}</span>
  ) : null;

  if (action === "Score") {
    return (
      <span>
        {action}&nbsp;{rankInfo}&nbsp;on {scenarioCount}
      </span>
    );
  }

  return (
    <span>
      {action} {scenarioCount} {frequency}
      {(numOfRequiredScenarios ?? numOfScenarios) > 1 && frequency
        ? " each"
        : ""}
      {scoreRequirement}
    </span>
  );
};

export function ProgramRoleplayCard({
  roleplayScenarios = [],
  canDoRoleplay = false,
  hideRoleplayRank = false,
  large = false,
  isProgramLead = false,
  isProgramClosed = false,
  minimumRank,
  minimumAttempts,
  maximumAttempts,
  scenarioRequiredNum,
  hasCompletedPrerequisites = true,
}: ProgramRoleplayCardProps) {
  const completionInstructions = getRoleplayCompletionInstructions({
    maximumAttempts,
    minimumAttempts,
    numOfRequiredScenarios: scenarioRequiredNum,
    numOfScenarios: roleplayScenarios?.length || 0,
    minimumRank,
  });

  return (
    <div
      className={cn("xs:gap-3.5 flex flex-col md:gap-4", {
        "max-w-180": !large,
      })}
    >
      <div className="flex items-center gap-4">
        <IconCircle
          icon="lightning"
          color={BLUE_TEXT_COLOR}
          backgroundColor={LIGHT_BLUE}
          size={36}
        />
        <div className="flex flex-col">
          <div className="text-lg font-semibold">Scenarios</div>
          <div className="text-subtext text-base font-normal flex items-center">
            {completionInstructions}
          </div>
        </div>
      </div>
      {sortScenariosByDifficulty(roleplayScenarios).map((scenario) => (
        <Box key={scenario.id}>
          <ScenarioCard
            scenario={scenario as Scenario}
            iconSize={60}
            showShortDesc={false}
            showPlayButton={canDoRoleplay}
            showRank={!hideRoleplayRank}
            isProgramRoleplay={true}
            isProgramLead={isProgramLead}
            isProgramClosed={isProgramClosed}
            showDifficulty={isProgramLead}
            showAttempts={
              !isProgramLead &&
              (!!minimumAttempts || !!scenario.roleplaySessions?.length)
            }
            showProgramStatus={!isProgramLead}
            maximumAttempts={maximumAttempts}
            minimumAttempts={minimumAttempts}
            numberOfAttempts={scenario.roleplaySessions?.length}
            minimumRank={minimumRank}
            isLocked={!hasCompletedPrerequisites && !isProgramLead}
          />
        </Box>
      ))}
    </div>
  );
}
